<template>
   <div>
      <div v-if="!uploadState" style="margin-bottom: 25px">
         <label class="mb-4 modal-subtitle">{{ importIntroText }}</label>
         <b-button
            style="
               background-color: transparent;
               color: #0978cd;
               border-color: #0978cd;
               font-size: 12px !important;
               padding: 5px 25px;
            "
            :disabled="isCompleted"
            :class="{'btn-disabled': isCompleted}"
            class="mb-4"
            @click="downloadTemplate"
            >{{ importTemplateButtonLabel }}</b-button
         >

         <label class="mb-4 modal-subtitle"
            >{{ importRequiredColumnsIntro }}
            <b>{{ importRequiredColumnsText }}</b>
         </label>
         <FileUploadComponent
            @file-selected="onFileSelected"
            :currentFile="newFile"
            acceptFileTypes=".csv,.xlsx,.xls"
            :disabled="isCompleted"
            :account-name-error="accountNameError"
         />
         <FilesList :category="category" :category-name="categoryName" />
         <div class="d-flex flex-row justify-content-end mt-4" style="width: 100%">
            <b-button
               class="my-next-ok-btn"
               :class="{'btn-disabled': nextDisabled}"
               variant="primary"
               @click="nextState"
               :disabled="nextDisabled || isCompleted"
               >Next
            </b-button>
         </div>
      </div>

      <div
         v-if="
            modalState === `configureFileUpload` &&
            uploadState === `mapHeader` &&
            pendingUpload.errors.length === 0 &&
            !successfulImport
         "
         style="margin-bottom: 25px"
      >
         <label style="font-size: 15px" class="font-weight-bold mb-2 mt-2">
            First, please choose the row with column headings if this file has one.
         </label>

         <div>
            <div class="no-header-container">
               <b-checkbox
                  id="checkbox-no-header"
                  :ref="`cb${-1}`"
                  :checked="headerRow === -1"
                  @change="selectHeaderRow(-1)"
                  :class="{
                     'table-hidden': hasHeaderRow,
                     'table-selected': headerRow === -1,
                  }"
               >
               </b-checkbox>
               <label class="no-header-label" for="checkbox-no-header">
                  This file does not contain headers
               </label>
            </div>

            <hr />

            <b-table
               id="table-header-row"
               class="scrollbar mb-0"
               :table-class="{'fade-out': hasFadeOut}"
               :fields="previewFields"
               :items="tableData"
               selectable
               hover
               :selected-variant="null"
               @row-clicked="(item, idx) => selectHeaderRow(idx)"
               thead-class="hide"
               responsive
               small
               borderless
            >
               <template #cell()="data">
                  <div
                     class="line-clamp-1"
                     style="min-width: 8rem"
                     :class="{
                        'table-hidden':
                           (headerRow !== null && headerRow === -1) ||
                           isBeforeSelectedHeaderRow(data.index),
                        'table-selected': hasHeaderRow && headerRow === data.index,
                     }"
                  >
                     {{ data.value }}
                  </div>
               </template>
               <template #cell(input)="data">
                  <b-checkbox
                     :id="`checkbox-header-row-${data.index + 1}`"
                     :ref="`cb${data.index}`"
                     :checked="data.index === headerRow"
                     class="checkbox-position"
                     @change="selectHeaderRow(data.index)"
                     :class="{
                        'table-hidden':
                           (headerRow !== null && headerRow === -1) ||
                           isBeforeSelectedHeaderRow(data.index),
                        'table-selected': headerRow === data.index,
                     }"
                  ></b-checkbox>
               </template>
            </b-table>
            <div class="d-flex flex-row justify-content-end mt-4" style="width: 100%">
               <b-button
                  variant="outline-primary"
                  class="back-button mr-2"
                  @click="backState"
                  style="font-size: 13px"
               >
                  <b-icon-arrow-left-short class="mr-1" />
                  Back
               </b-button>
               <b-button
                  class="my-next-ok-btn"
                  :class="{'btn-disabled': nextDisabled}"
                  variant="primary"
                  @click="nextState"
                  :disabled="nextDisabled"
                  >Next
               </b-button>
            </div>
         </div>
      </div>

      <div
         v-if="
            modalState === `configureFileUpload` &&
            uploadState === `mapFirstRow` &&
            pendingUpload.errors.length === 0 &&
            !successfulImport
         "
         style="margin-bottom: 25px"
      >
         <label style="font-size: 15px" class="font-weight-bold mb-2 mt-2">
            Next, please pick the row we should start with (in case you want to ignore some rows
            that come after the header).
         </label>

         <div>
            <b-table
               id="table-first-data-row"
               class="scrollbar mb-0"
               :table-class="{'fade-out': hasFadeOut}"
               :tbody-tr-class="dataRowRowClass"
               :fields="previewFields"
               :items="tableData"
               selectable
               :selected-variant="null"
               hover
               @row-clicked="(item, idx) => selectFirstDataRow(idx)"
               thead-class="hide"
               responsive
               small
               borderless
            >
               <template #cell()="data">
                  <div
                     class="line-clamp-1"
                     style="min-width: 8rem"
                     :class="{
                        'table-hidden': isBeforeSelectedFirstDataRow(data.index),
                     }"
                  >
                     {{ data.value }}
                  </div>
               </template>
               <template #cell(input)="data">
                  <b-checkbox
                     v-if="!hasHeaderRow || data.index > 0"
                     class="checkbox-position"
                     :id="`checkbox-data-row-${data.index + 1}`"
                     :ref="`fdr-cb${data.index}`"
                     :checked="data.index === firstDataRow"
                     @change="selectFirstDataRow(data.index)"
                     :class="{
                        'table-hidden': isBeforeSelectedFirstDataRow(data.index),
                     }"
                  />
               </template>
            </b-table>
            <div class="d-flex flex-row justify-content-end mt-4" style="width: 100%">
               <b-button
                  variant="outline-primary"
                  class="back-button mr-2"
                  @click="backState"
                  style="font-size: 13px"
               >
                  <b-icon-arrow-left-short class="mr-1" />
                  Back
               </b-button>
               <b-button
                  class="my-next-ok-btn"
                  :class="{'btn-disabled': nextDisabled}"
                  variant="primary"
                  @click="nextState"
                  :disabled="nextDisabled"
                  >Next
               </b-button>
            </div>
         </div>
      </div>

      <div
         v-if="
            modalState === `configureFileUpload` &&
            uploadState === `mapCVS` &&
            pendingUpload.errors.length === 0 &&
            !successfulImport
         "
         style="margin-bottom: 25px"
      >
         <label style="font-size: 15px" class="font-weight-bold mb-2 mt-2">
            Finally, please map your columns to the data we need.
         </label>
         <p class="mb-4 modal-subtitle">
            The following fields are <b>required</b>:
            <template v-if="hasManyLocations && expenseType !== 'employees'">
               <b>
                  Vendor Name, Location (US State), Transaction Date, Transaction Amount (Debit).
               </b>
            </template>
            <template v-else-if="expenseType === 'employees'">
               <b>Full Name, Location (US State), Federal Taxable Wages, and Paycheck Date.</b>
               Optionally, you can also include
               <b>Unique Employee ID, Title, Department, Subsidiary and Officer.</b>
            </template>
            <template v-else>
               <b>
                  {{
                     hasManyLocations
                        ? 'Vendor Name, Location (US State), Transaction Date, Transaction Amount (Debit).'
                        : 'Vendor Name, Transaction Date, Transaction Amount (Debit).'
                  }}
               </b>
            </template>
         </p>

         <b-table
            id="table-map-columns"
            class="scrollbar mb-0"
            :table-class="{'fade-out': hasFadeOut}"
            :items="tableData"
            :fields="visibleMappingFields"
            responsive
            borderless
            small
         >
            <template #head()="data">
               <b-form-select
                  :id="`select-column-${parseInt(data.column) + 1}`"
                  :options="colOptions"
                  style="min-width: 11rem; border-color: grey; font-size: 13px"
                  v-model="mapping[data.column]"
                  @change="(val) => mappingSelected(data.column, val)"
               ></b-form-select>
            </template>
            <template #cell()="data">
               <span style="font-size: 13px">
                  {{ data.value }}
               </span>
            </template>
         </b-table>
         <div class="d-flex flex-row justify-content-end mt-4" style="width: 100%">
            <b-button
               variant="outline-primary"
               class="back-button mr-2"
               @click="backState"
               style="font-size: 13px"
            >
               <b-icon-arrow-left-short class="mr-1" />
               Back
            </b-button>
            <b-button
               class="my-next-ok-btn"
               :class="{'btn-disabled': nextDisabled}"
               variant="primary"
               @click="nextState"
               :disabled="nextDisabled"
               >{{
                  modalState === `uploadVendors` ||
                  (modalState === `configureFileUpload` &&
                     uploadState !== `mapCVS` &&
                     uploadState !== null)
                     ? `Next`
                     : `Import`
               }}
            </b-button>
         </div>
      </div>

      <div
         v-if="modalState === `configureFileUpload` && pendingUpload.errors.length > 0"
         style="margin-bottom: 25px"
      >
         <label style="font-size: 15px" class="font-weight-bold mb-2 mt-2">Import failed ⚠️</label>

         <p class="mb-4 modal-subtitle">
            There was an issue importing your vendors list.<br />
            Please make sure all required columns are filled and correctly formatted before
            uploading again.
         </p>

         <div class="alert-box mb-2">
            <div class="icon">⚠️</div>
            <div class="content">
               <strong style="font-size: 12px">Failed to Import</strong>
               <div v-if="Array.isArray(pendingUpload.errors)">
                  <div
                     v-for="(error, index) in pendingUpload.errors"
                     style="font-size: 12px"
                     :key="index"
                  >
                     • {{ formatErrorMsg(error) }}
                  </div>
               </div>
               <div v-if="typeof pendingUpload.errors === 'string'" style="font-size: 12px">
                  • {{ pendingUpload.errors }}
               </div>
            </div>
         </div>

         <b-table
            id="table-map-columns"
            class="scrollbar mb-0"
            :table-class="{'fade-out': hasFadeOut}"
            :items="tableData"
            :fields="visibleMappingFields"
            responsive
            borderless
            small
         >
            <template #head()="data">
               <b-form-select
                  :id="`select-column-${parseInt(data.column) + 1}`"
                  :options="colOptions"
                  style="min-width: 11rem; border-color: grey; font-size: 13px"
                  v-model="mapping[data.column]"
                  @change="(val) => mappingSelected(data.column, val)"
                  disabled
               ></b-form-select>
            </template>
            <template #cell()="data">
               <span
                  style="font-size: 13px"
                  :class="{
                     'table-selected': hasHeaderRow && 0 === data.index,
                  }"
               >
                  {{ data.value }}
               </span>
            </template>
         </b-table>
         <div class="d-flex flex-row justify-content-end mt-4">
            <b-button variant="outline-primary" class="back-button mr-2" @click="backState">
               <b-icon-arrow-left-short class="mr-1" />
               Back
            </b-button>
         </div>
      </div>

      <div
         v-if="modalState === `configureFileUpload` && successfulImport"
         style="margin-bottom: 25px"
      >
         <label style="font-size: 15px" class="font-weight-bold mb-2 mt-2">
            Import completed successfully ✅
         </label>

         <p class="mb-4 modal-subtitle">
            Your vendors list has been imported and all CSV columns were mapped correctly.<br />
            Please proceed with providing vendors, or click Finish if you're Finished.
         </p>

         <div class="success-box mb-2 d-flex align-items-center">
            <div class="icon">✅</div>
            <div style="font-size: 12px">
               Successfully imported:
               {{ importedRecords }}
               {{ importedRecords === 1 ? 'record' : 'records' }}
               {{
                  skippedRecords > 0
                     ? ` (${skippedRecords} ${skippedRecords === 1 ? 'was' : 'were'} skipped)`
                     : ''
               }}
            </div>
         </div>
         <b-table
            id="table-map-columns"
            class="scrollbar mb-0"
            :table-class="{'fade-out': hasFadeOut}"
            :items="tableData"
            :fields="visibleMappingFields"
            responsive
            borderless
            small
         >
            <template #head()="data">
               <b-form-select
                  :id="`select-column-${parseInt(data.column) + 1}`"
                  :options="colOptions"
                  style="min-width: 11rem; border-color: grey; font-size: 13px"
                  v-model="mapping[data.column]"
                  @change="(val) => mappingSelected(data.column, val)"
                  disabled
               ></b-form-select>
            </template>
            <template #cell()="data">
               <span
                  style="font-size: 13px"
                  :class="{
                     'table-selected': hasHeaderRow && 0 === data.index,
                  }"
               >
                  {{ data.value }}
               </span>
            </template>
         </b-table>
         <div class="d-flex flex-row justify-content-end mt-4" style="width: 100%">
            <b-button class="my-next-ok-btn" variant="primary" @click="$emit('finish')">
               Finish
            </b-button>
         </div>
      </div>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {ADD_VENDOR_IMPORT_CONFIG, EXPENSE_DATA_UPLOAD_CATEGORY_CONFIG} from '@/helpers/baseConfig';
import {normalizeStringInputValue} from '@/helpers/utils';
import FileUploadComponent from './FileUploadComponent.vue';
import FilesList from './FilesList.vue';

export default {
   components: {
      FilesList,
      FileUploadComponent,
   },

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         modalState: 'uploadVendors',
         uploadState: null,
         importedRecords: 0,
         skippedRecords: 0,
         fileInput: '',
         newFile: {
            file: null,
            description: null,
         },
         headerRow: null,
         firstDataRow: null,
         mapping: {},
         successfulImport: false,
         uploading: false,
      };
   },

   computed: {
      ...mapGetters({
         currentCompany: 'companies/currentCompany',
         studyPeriodMap: 'companies/studyPeriodMap',
      }),

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },

      pendingUpload() {
         return (
            this.$store.getters[`${this.uploadModule}/pendingUpload`] || {
               errors: [],
               columnCount: 0,
               previewRows: [],
            }
         );
      },

      uploadedFiles() {
         return this.$store.getters[`${this.uploadModule}/uploadedFiles`] || [];
      },

      companyId() {
         return this.currentCompany.id;
      },

      uploadModule() {
         return `${this.expenseType}Uploads`;
      },

      visibleMappingFields() {
         const nonEmptyCols = this.getNonEmptyColumns(this.tableData);

         return this.previewFields
            .filter((f) => nonEmptyCols.has(f.key))
            .map((f) => ({
               key: f.key,
               label: '',
            }));
      },

      previewFields() {
         const fields = [...Array(this.pendingUpload.columnCount).keys()].map((idx) => {
            return {
               key: `${idx}`,
               label: '',
            };
         });
         return [{key: 'input', label: ''}, ...fields];
      },

      tableData() {
         const preview = this.pendingUpload.previewRows ?? [];
         return this.transformRows(this.sliceRowsForState(preview));
      },

      hasFadeOut() {
         return this.tableData.length >= 5;
      },

      colOptions() {
         return [
            {value: null, text: '-'},
            ...this.fileImportColumns.map((col) => ({value: col.key, text: col.label})),
         ];
      },

      requiredColumnsMapped() {
         const cols = this.fileImportColumns
            .filter((col) => col.required)
            .map((col) => col.key)
            .reduce((obj, col) => {
               obj[col] = false;
               return obj;
            }, {});

         Object.values(this.mapping).forEach((val) => {
            Object.keys(cols).forEach((col) => (cols[col] = cols[col] || val === col));
         });

         return Object.values(cols).every((val) => val);
      },

      periodNames() {
         const supportedPeriodIds = this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
         return supportedPeriodIds
            .map((id) => this.studyPeriodMap[id]?.label)
            .filter((label) => !!label);
      },

      prettyPeriodNames() {
         const formatter = new Intl.ListFormat('en', {
            style: 'long',
            type: 'disjunction',
         });
         return formatter.format(this.periodNames);
      },

      hasManyLocations() {
         return this.declaration === 'MANY';
      },

      hasHeaderRow() {
         return this.headerRow != null && this.headerRow >= 0;
      },

      importCopyConfig() {
         return ADD_VENDOR_IMPORT_CONFIG[this.expenseType] || ADD_VENDOR_IMPORT_CONFIG.supplies;
      },

      importIntroText() {
         return this.importCopyConfig.intro({prettyPeriodNames: this.prettyPeriodNames});
      },

      importTemplateButtonLabel() {
         return this.importCopyConfig.templateButtonLabel;
      },

      importRequiredColumnsIntro() {
         return this.importCopyConfig.requiredColumnsIntro;
      },

      importRequiredColumnsText() {
         return this.importCopyConfig.requiredColumns({hasManyLocations: this.hasManyLocations});
      },

      downloadTemplateAction() {
         const actionMap = {
            supplies: 'exports/downloadVendorTemplate',
            cloud: 'cloudUploads/downloadTemplate',
            contractors: null,
            employees: 'employeesUploads/downloadTemplate',
         };

         return actionMap[this.expenseType] || null;
      },

      fileImportColumns() {
         if (this.expenseType === 'employees') {
            return [
               {key: 'fullname', label: 'Full Name', required: true},
               {key: 'location', label: 'Location (US State)', required: true},
               {key: 'payroll', label: 'Federal Taxable Wages', required: true},
               {key: 'paycheck_date', label: 'Paycheck Date', required: true},
               {key: 'ident', label: 'Employee Id', required: false},
               {key: 'title', label: 'Title', required: false},
               {key: 'department', label: 'Department', required: false},
               {key: 'subsidiary', label: 'Subsidiary', required: false},
               {key: 'officer', label: 'Officer', required: false},
            ];
         }

         const cols = [{key: 'vendor', label: 'Vendor Name', required: true}];
         if (this.hasManyLocations) {
            cols.push({
               key: 'location',
               label: 'Location (US State)',
               labelLong: 'Location (US State) where supplies were consumed',
               required: true,
            });
         }
         cols.push({key: 'purchased', label: 'Transaction Date', required: true});
         cols.push({key: 'debit', label: 'Transaction Amount (Debit)', required: true});
         cols.push({key: 'credit', label: 'Refund Amount (Credit)', required: false});
         cols.push({key: 'description', label: 'Description', required: false});

         return cols;
      },

      normalizedAccountName() {
         return normalizeStringInputValue(this.newFile.description);
      },

      duplicateAccountName() {
         if (!this.normalizedAccountName) {
            return null;
         }

         const duplicate = this.uploadedFiles.find((file) => {
            return normalizeStringInputValue(file.description) === this.normalizedAccountName;
         });

         return duplicate ? duplicate.description : null;
      },

      accountNameError() {
         if (!this.duplicateAccountName) {
            return null;
         }

         return `Account name already exists: ${this.duplicateAccountName}`;
      },

      nextDisabled() {
         if (this.uploading) {
            return true;
         }
         if (this.modalState === 'uploadVendors') {
            return !this.normalizedAccountName || !!this.duplicateAccountName;
         }

         if (
            this.modalState === `configureFileUpload` &&
            this.uploadState === `mapHeader` &&
            this.headerRow === null
         ) {
            return true;
         }

         if (
            this.modalState === `configureFileUpload` &&
            this.uploadState === `mapFirstRow` &&
            this.firstDataRow === null
         ) {
            return true;
         }
         if (
            this.modalState === `configureFileUpload` &&
            this.uploadState === `mapCVS` &&
            !this.requiredColumnsMapped
         ) {
            return true;
         }
         return false;
      },

      categoryName() {
         return EXPENSE_DATA_UPLOAD_CATEGORY_CONFIG[this.expenseType]?.name || null;
      },
      category() {
         return EXPENSE_DATA_UPLOAD_CATEGORY_CONFIG[this.expenseType]?.category || null;
      },
   },

   watch: {
      modalState: {
         immediate: true,
         handler(state) {
            this.$emit('modal-state-change', state);
         },
      },
      nextDisabled: {
         immediate: true,
         handler(disabled) {
            this.$emit('next-disabled', disabled);
         },
      },
   },

   methods: {
      resetState() {
         this.modalState = 'uploadVendors';
         if (this.pendingUpload) this.pendingUpload.errors = [];
         this.fileInput = '';
         this.newFile = {
            file: null,
            description: null,
         };
         this.headerRow = null;
         this.firstDataRow = null;
         this.mapping = {};
         this.successfulImport = false;
         this.uploadState = null;
         this.importedRecords = 0;
         this.skippedRecords = 0;
         this.uploading = false;
      },

      getNonEmptyColumns(rows) {
         return rows.reduce((cols, row) => {
            Object.entries(row).forEach(([key, value]) => {
               if (this.isValidDataField(key, value)) {
                  cols.add(key);
               }
            });
            return cols;
         }, new Set());
      },
      isValidDataField(key, value) {
         return !this.isIgnoredKey(key) && value !== null && value !== undefined && value !== '';
      },

      isIgnoredKey(key) {
         return key === 'index' || key === '_rowVariant';
      },

      selectHeaderRow(idx) {
         this.$nextTick(() => {
            if (idx === this.headerRow) {
               this.headerRow = null;
            } else {
               this.headerRow = idx;
               // Hack to make sure the checkbox stays checked
               if (this.$refs[`cb${idx}`]) {
                  this.$refs[`cb${idx}`].localChecked = true;
               }
            }
         });
      },

      sliceRowsForState(sourceRows) {
         switch (this.uploadState) {
            case 'mapHeader':
               return sourceRows;

            case 'mapFirstRow': {
               const hasHeader = this.headerRow >= 0;
               return hasHeader
                  ? [sourceRows[this.headerRow], ...sourceRows.slice(this.headerRow + 1)]
                  : sourceRows;
            }

            case 'mapCVS':
            case 'done': {
               const hasHeader = this.headerRow >= 0;
               if (hasHeader) {
                  const header = sourceRows[this.headerRow];
                  const dataRows = sourceRows.slice(this.firstDataRow + this.headerRow);
                  return [header, ...dataRows];
               }
               return sourceRows.slice(this.firstDataRow);
            }

            default:
               return [];
         }
      },

      transformRows(rows) {
         return rows.map((row, rowIdx) => {
            const rowObj = this.buildRowObject(row);
            rowObj.index = rowIdx;
            this.setRowVariant(rowObj, rowIdx);
            return rowObj;
         });
      },

      buildRowObject(row) {
         return row.reduce((obj, item, colIdx) => {
            obj[colIdx] = item;
            return obj;
         }, {});
      },

      setRowVariant(rowObj, rowIdx) {
         rowObj._rowVariant = null;

         if (this.uploadState === 'mapHeader' && rowIdx === this.headerRow) {
            rowObj._rowVariant = 'selected';
         } else if (this.uploadState === 'mapFirstRow' && rowIdx === this.firstDataRow) {
            rowObj._rowVariant = 'selected';
         } else if (this.uploadState === 'mapCVS' && rowIdx === 0 && this.headerRow >= 0) {
            rowObj._rowVariant = 'selected';
         }
      },

      onFirstDataRowSelected() {
         this.$store.commit(`${this.uploadModule}/setFirstDataRow`, {
            firstDataRow: this.firstDataRow + (this.headerRow >= 0 ? this.headerRow : 0),
         });
      },
      backState(evt) {
         evt.preventDefault();

         if (this.modalState === 'configureFileUpload' && this.pendingUpload.errors.length > 0) {
            this.pendingUpload.errors = [];
            if (this.uploadState === 'done') {
               this.uploadState = 'mapCVS';
            }
            return (this.modalState = 'configureFileUpload');
         }

         if (this.uploadState === 'done' && this.modalState === 'configureFileUpload') {
            this.pendingUpload.errors = [];
            this.uploadState = 'mapCVS';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.uploadState === 'mapCVS' && this.modalState === 'configureFileUpload') {
            this.uploadState = 'mapFirstRow';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.uploadState === 'mapFirstRow' && this.modalState === 'configureFileUpload') {
            this.uploadState = 'mapHeader';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.modalState === 'configureFileUpload' && this.uploadState === 'mapHeader') {
            this.uploadState = null;
            return (this.modalState = 'uploadVendors');
         }
      },
      async nextState(evt) {
         evt.preventDefault();

         if (this.modalState === 'uploadVendors') {
            await this.uploadInitialFile();
         }

         if (this.uploadState === 'mapCVS' && this.modalState === 'configureFileUpload') {
            try {
               await this.uploadFile();
            } catch {
               return;
            }
            this.uploadState = 'done';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.uploadState === 'mapFirstRow' && this.modalState === 'configureFileUpload') {
            this.uploadState = 'mapCVS';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.uploadState === 'mapHeader' && this.modalState === 'configureFileUpload') {
            this.uploadState = 'mapFirstRow';
            return (this.modalState = 'configureFileUpload');
         }

         if (this.modalState === 'uploadVendors') {
            this.uploadState = 'mapHeader';
            return (this.modalState = 'configureFileUpload');
         }
      },
      startNewImport() {
         this.clearImport();
      },
      dataRowRowClass(item) {
         if (this.hasHeaderRow && item.index === 0) {
            return 'header-row-bold';
         }

         if (item.index <= this.headerRow) {
            return 'no-hover';
         }

         return '';
      },

      clearImport() {
         this.newFile.file = null;
         this.newFile.description = null;
         this.$store.commit(`${this.uploadModule}/newImport`);
      },

      async uploadInitialFile() {
         try {
            await this.$store.dispatch(`${this.uploadModule}/importFile`, {
               ...this.newFile,
            });
         } catch (err) {
            if (err.message === 'InsufficientColumns') {
               const fileSelect = await this.$bvModal.msgBoxConfirm(
                  "The file you selected doesn't have enough columns to contain all the required data. Please check the file and try again.",
                  {
                     title: 'Insufficient Columns',
                     centered: true,
                     okTitle: 'Select a different file',
                  }
               );
               if (fileSelect) {
                  this.startNewImport();
               }
            }
            return;
         }
      },

      async onFileSelected(file) {
         this.resetState();

         const selectedFile = file?.file;
         if (!selectedFile) return;

         if (selectedFile.size === 0) {
            this.fileInput = file.input;
            const fileSelect = await this.$bvModal.msgBoxConfirm(
               'The file you selected is empty. Please check the file and try again.',
               {
                  title: 'Empty File',
                  centered: true,
                  okTitle: 'Select a different file',
               }
            );
            if (fileSelect) {
               this.startNewImport();
            }
            this.fileInput = '';
            return;
         }

         this.newFile.file = file.file;
         this.newFile.description = file.input;
      },

      selectFirstDataRow(idx) {
         // When there is no header selected (headerRow < 0), row 0 is a valid first data row.
         // When a header is selected (headerRow >= 0), row 0 must not be selectable.
         if (idx === 0 && this.hasHeaderRow) return;

         this.$nextTick(() => {
            if (idx === this.firstDataRow) {
               this.firstDataRow = null;
            } else {
               this.firstDataRow = idx;
               this.$refs[`fdr-cb${idx}`].localChecked = true;
            }
         });
      },

      mappingSelected(idx, val) {
         const match = Object.entries(this.mapping).find(
            (entry) => entry[0] !== idx && entry[1] === val
         );
         if (match) {
            this.mapping[match[0]] = null;
         }
      },

      async uploadFile() {
         this.uploading = true;
         this.onFirstDataRowSelected();
         this.onHeaderRowSelected();
         const mapping = Object.entries(this.mapping)
            .filter(([, val]) => val !== null && val !== undefined)
            .reduce((obj, [colIndex, key]) => {
               obj[key] = Number(colIndex);
               return obj;
            }, {});

         this.$store.commit(`${this.uploadModule}/setMapping`, {mapping});
         try {
            const response = await this.blockingRequest(`${this.uploadModule}/uploadFile`, {
               mapping,
               companyId: this.companyId,
            });
            const targetUpload = Array.isArray(response)
               ? response.find(({description}) => description === this.newFile.description)
               : null;

            if (!targetUpload?.validation?.post) {
               this.pendingUpload.errors = [
                  'The import finished, but the server did not return a usable result. Please try again.',
               ];
               throw new Error('Missing upload result');
            }

            this.importedRecords = targetUpload.validation.post.records ?? 0;
            this.skippedRecords = targetUpload.validation.post.skipped ?? 0;

            this.successfulImport = true;
            this.uploadState = 'done';
         } finally {
            this.uploading = false;
         }
      },

      formatErrorMsg(data) {
         if (data.error === 'No records found') {
            return `No records found with a purchase date within the time period${
               this.periodNames.length === 1 ? '' : 's'
            } ${this.prettyPeriodNames}`;
         }
         return data.error;
      },

      onHeaderRowSelected() {
         this.$store.commit(`${this.uploadModule}/setHeaderRow`, {headerRow: this.headerRow});
      },

      isBeforeSelectedFirstDataRow(index) {
         return (
            this.firstDataRow !== null &&
            index < this.firstDataRow &&
            !(this.headerRow >= 0 && index === 0)
         );
      },

      isBeforeSelectedHeaderRow(index) {
         return this.headerRow >= 0 && index < this.headerRow;
      },

      async downloadTemplate() {
         if (!this.downloadTemplateAction) {
            this.$store.commit('showAlert', {
               msg: `Template download is not supported for ${this.expenseType} yet.`,
               seconds: 5,
            });
            return;
         }

         await this.$store.dispatch(this.downloadTemplateAction);
      },

      async loadData() {
         try {
            await this.$store.dispatch(`${this.uploadModule}/loadUploadCategory`, {
               companyId: this.companyId,
            });
         } catch (error) {
            console.log(error);
         }
      },
   },
   async mounted() {
      await this.loadData();
   },
};
</script>

<style lang="scss" scoped>
.modal-subtitle {
   padding-top: 10px;
   font-size: 14px;
   font-weight: 400;
   color: black;
   margin-right: 20px;
}

.checkbox-position {
   position: relative;
   top: -3px;
}
::v-deep #table-first-data-row tbody tr.header-row-bold td {
   font-weight: 800;
}

.back-button {
   padding: 5px 20px;
   font-size: 14px !important;
   background-color: transparent !important;
   color: #1660a9 !important;

   &:hover {
      background-color: transparent !important;
      color: #1660a9 !important;
   }
}

.my-next-ok-btn {
   padding: 5px 30px;
   font-size: 13px !important;
   background-color: #0072cc;
   border: 1px solid #1660a9;
   &:hover {
      background-color: #0072cc;
      border: 1px solid #1660a9;
   }
}

.success-box {
   display: flex;
   align-items: flex-start;
   border: 1px solid #25b26c;
   background-color: #f8fcfa;
   padding: 12px 16px;
   border-radius: 6px;
}

.alert-box {
   display: flex;
   align-items: flex-start;
   border: 1px solid #f8d7a3;
   background-color: #fffdf5;
   padding: 12px 16px;
   border-radius: 6px;
}
.icon {
   margin-right: 8px;
   font-size: 0.7rem;
   color: #e6a100;
}
.content strong {
   display: block;
   margin-bottom: 4px;
}

.no-header-container {
   display: flex;
   flex-direction: row;
   align-items: center;
}

.table-responsive {
   height: 40dvh;
}

::v-deep #table-header-row,
::v-deep #table-first-data-row,
::v-deep #table-map-columns,
::v-deep .no-header-container {
   @for $row from 1 through 3 {
      font-size: 12px;
      &.fade-out tr:nth-last-child(#{$row}) {
         color: lighten($black, percentage(1 - ($row * 0.25)));

         .custom-control-label::before {
            border-color: lighten($gray-800, percentage(0.8 - ($row * 0.2)));
         }
      }
   }

   .table-selected,
   .table-selected .custom-control-label {
      font-weight: 800;
      color: $black !important;
   }

   .table-hidden {
      color: $gray-450 !important;

      .custom-control-label::before {
         border-color: $gray-450 !important;
      }

      .custom-control-input:checked ~ .custom-control-label::before {
         background-color: $gray-450 !important;
      }
   }

   &.table-hover > tbody > tr.no-hover:hover > td {
      background-color: $white;
      cursor: auto;
   }
}
::v-deep #table-map-columns,
::v-deep #table-first-data-row {
   @for $row from 1 through 3 {
      font-size: 12px;
      &.fade-out tr:nth-last-child(#{$row}) {
      }
   }

   .table-selected,
   .table-selected .custom-control-label {
      font-weight: 800;
      color: $black !important;
   }

   .table-hidden {
      .custom-control-label::before {
      }

      .custom-control-input:checked ~ .custom-control-label::before {
      }
   }

   &.table-hover > tbody > tr.no-hover:hover > td {
      background-color: $white;
      cursor: auto;
   }
}

.btn-disabled {
   background-color: #999 !important;
   border-color: #999 !important;
   &:hover {
      background-color: #999 !important;
      border-color: #999 !important;
   }
}

.no-header-label {
   margin-bottom: 0;
}
</style>
